/**
 * useNodeIdentityBorderTrace — one NAD feature tag for "what colour did this
 * surface paint for this node's identity?".
 *
 * Workplace post 1579940090429955: the variant picker's selected border did not
 * match the node card border (character card yellow / picker purple, object card
 * amber / picker cyan). Both surfaces now derive from `AGENT_COLORS[agent]`, but
 * that is only *reviewable* if both emit the same, joinable event.
 *
 * Every emitter uses the SAME feature tag and the SAME field names, with a
 * `surface` discriminator, so a single command produces the comparison:
 *
 *   ./nad trace --feature node-identity-border --last 10m
 *
 * For one `node_id` you get a `surface=card` row and a `surface=variant-picker`
 * row; `identityColorHex` must be equal, and when `borderState=selected` the
 * card's `appliedBorderColor` must equal the picker's too. A future divergence
 * shows up as two different hexes on the same `node_id` rather than as a colour
 * nobody notices.
 *
 * `createNadDebug` is dev-gated on `VITE_NAD_DEBUG`, so this costs nothing in
 * production. Emission is keyed on the decision inputs (not per render) to keep
 * the trace readable.
 */
import { useEffect } from 'react'
import { createNadDebug } from '../../../../utils/debugLog'
import type { AgentColorConfig } from '../../../../constants/agentColors'

const identityBorderLog = createNadDebug('dw:node-identity-border')

/** Which UI surface painted the border. */
export type IdentityBorderSurface = 'card' | 'variant-picker'

/**
 * Which state the node card border is in. The card intentionally paints a
 * dimmed/alpha variant when idle or hovered, so a reviewer comparing the card
 * to the picker must only compare the `selected` rows — this field makes that
 * filter explicit in the trace instead of implicit in a side script.
 */
export type IdentityBorderState = 'selected' | 'running' | 'hovered' | 'idle' | 'error'

export function resolveIdentityBorderState(
  selected: boolean,
  status: string,
  isHovered: boolean,
): IdentityBorderState {
  if (status === 'error') return 'error'
  if (selected) return 'selected'
  if (status === 'running' || status === 'pending_execution' || status === 'pending_cost_confirmation') {
    return 'running'
  }
  return isHovered ? 'hovered' : 'idle'
}

export interface NodeIdentityBorderTrace {
  /** Canvas node id — the join key between the two surfaces. */
  nodeId: string
  /** Node type, so the trace stands alone without a fixture-name convention. */
  nodeType: string
  surface: IdentityBorderSurface
  /** The node's identity colour. Must match across surfaces for one node. */
  agentColor: AgentColorConfig
  /** The colour actually written to CSS on this surface. */
  appliedBorderColor: string | undefined
  /** Card only — which branch of the border state machine produced it. */
  borderState?: IdentityBorderState
  /** Variant picker only — which cell the border was painted on. */
  variantIndex?: number
}

/**
 * States in which the surface is expected to paint the FULL identity colour.
 * Idle/hovered cards deliberately paint a dimmed alpha variant, so they are not
 * comparable against the picker and are reported as matchesIdentity=n/a rather
 * than as failures.
 */
function isFullIdentityState(borderState: IdentityBorderState): boolean {
  return borderState === 'selected' || borderState === 'running'
}

export function useNodeIdentityBorderTrace({
  nodeId,
  nodeType,
  surface,
  agentColor,
  appliedBorderColor,
  borderState,
  variantIndex,
}: NodeIdentityBorderTrace): void {
  const identityColorHex = agentColor.active
  useEffect(() => {
    const state = borderState ?? 'selected'
    const comparable = isFullIdentityState(state)
    identityBorderLog({
      event: 'identity-border-resolved',
      node_id: nodeId,
      node_type: nodeType,
      surface,
      identityColorHex,
      appliedBorderColor,
      borderState: state,
      // Verdict in-log, so reviewing the fix is `grep matchesIdentity=false`
      // (expect hits before the fix, none after) rather than eyeballing hexes.
      expectedBorderColor: comparable ? identityColorHex : 'dimmed-variant',
      matchesIdentity: comparable ? String(appliedBorderColor === identityColorHex) : 'n/a',
      ...(variantIndex === undefined ? {} : { variantIndex, cellState: 'selected' }),
    })
  }, [nodeId, nodeType, surface, identityColorHex, appliedBorderColor, borderState, variantIndex])
}
